import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table, type TableColumn } from "@/components/Table";
import { Badge } from "@/components/Badge";
import { Icon } from "@/components/Icon";
import { date, price, getAvatar, quantity } from "@/utils/format";
import type { AiUsageItem, AiUsageFilters } from "@/pages/AiConsumption/types";
import { parseListSelection } from "@/components/listPreferences";
import { Button } from "@/components/Button";

interface AiConsumptionTableProps {
    items: AiUsageItem[];
    loading: boolean;
    filters: AiUsageFilters;
    onFilters: (filters: AiUsageFilters) => void;
    filterControls: React.ReactNode;
}

export const AiConsumptionTable: React.FC<AiConsumptionTableProps> = ({
    items,
    loading,
    filters,
    onFilters,
    filterControls,
}) => {
    const { t } = useTranslation("global");

    const getContextBadgeVariant = (
        type: string | null
    ): "info" | "success" | "warning" | "primary" | "neutral" => {
        switch (type) {
            case "design":
                return "primary";
            case "analysis":
                return "warning";
            case "jira":
                return "info";
            case "suggestions":
                return "warning";
            case "requirements":
            case "requirements_document":
                return "success";
            default:
                return "neutral";
        }
    };

    const getContextLabel = (type: string | null): string => {
        if (!type) {
            return "-";
        }
        const key = `ai_consumption.context_types.${type}`;
        const translated = t(key);
        if (translated && translated !== key) {
            return translated;
        }
        return type;
    };

    const columns: TableColumn<AiUsageItem>[] = useMemo(
        () => [
            {
                key: "created_at",
                label: t("ai_consumption.col_date"),
                sortable: true,
                render: (row) => (
                    <div className="whitespace-nowrap">
                        {date(row.created_at, true)}
                    </div>
                ),
            },
            {
                key: "user",
                label: t("ai_consumption.col_user"),
                sortable: true,
                sortKey: "user.name",
                render: (row) => {
                    const name = row.user?.name || "Desconocido";
                    return (
                        <div className="flex items-center gap-2 min-w-[140px]">
                            <div className="min-w-0">
                                <div className="font-semibold text-slate-800 dark:text-zinc-200 truncate">
                                    {name}
                                </div>
                                {row.user?.email && (
                                    <div className="text-[10px] text-slate-400 dark:text-zinc-500 truncate">
                                        {row.user.email}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                },
            },
            {
                key: "development",
                label: t("ai_consumption.col_dev"),
                sortable: true,
                sortKey: "development.code",
                render: (row) => {
                    if (!row.development) {
                        return <span className=""></span>;
                    }
                    return (
                        <Badge
                            value={row.development.code}
                            link={`/developments/${row.development_id}`}
                        />
                    );
                },
            },
            {
                key: "context_type",
                label: t("ai_consumption.col_context"),
                sortable: true,
                render: (row) => (
                    <Badge variant={getContextBadgeVariant(row.context_type)}>
                        {getContextLabel(row.context_type)}
                    </Badge>
                ),
            },
            {
                key: "model",
                label: t("ai_consumption.col_provider_model"),
                sortable: true,
                render: (row) => (
                    <div className="flex flex-col gap-0.5">
                        <div className="font-semibold text-slate-800 dark:text-zinc-200 truncate">
                            {row.provider}
                        </div>
                        <div className="text-xs text-slate-400 dark:text-zinc-500 truncate">
                            {row.model}
                        </div>
                    </div>
                ),
            },
            {
                key: "input_tokens",
                label: t("ai_consumption.col_in_tokens"),
                sortable: true,
                align: "right",
                render: (row) => (
                    <span className="">{quantity(row.input_tokens)}</span>
                ),
            },
            {
                key: "output_tokens",
                label: t("ai_consumption.col_out_tokens"),
                sortable: true,
                align: "right",
                render: (row) => (
                    <span className="">{quantity(row.output_tokens)}</span>
                ),
            },
            {
                key: "total_tokens",
                label: t("ai_consumption.col_total_tokens"),
                sortable: true,
                align: "right",
                render: (row) => (
                    <span className="">{quantity(row.total_tokens)}</span>
                ),
            },
            {
                key: "cost",
                label: t("ai_consumption.col_cost"),
                sortable: true,
                align: "right",
                render: (row) => (
                    <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                        {price(row.cost, "euro")}
                    </span>
                ),
            },
        ],
        [t]
    );

    const itemsWithSearch = useMemo(() => {
        return items.map((item) => {
            const devText = item.development
                ? `${item.development.code} ${item.development.title}`
                : "";
            const userText = item.user
                ? `${item.user.name} ${item.user.email}`
                : "";
            const contextText = getContextLabel(item.context_type);

            return {
                ...item,
                _search_text:
                    `${devText} ${userText} ${item.provider} ${item.model} ${contextText}`.toLowerCase(),
            };
        });
    }, [items, t]); // include t so it recalculates on language change

    return (
        <Table<AiUsageItem & { _search_text?: string }>
            viewKey="vigepra_ai"
            searchValue={filters.search}
            onSearchChange={(search) => onFilters({ ...filters, search })}
            filters={filterControls}
            onClearViewFilters={() =>
                onFilters({
                    ...filters,
                    period: "all",
                    user_ids: [],
                    providers: [],
                    models: [],
                    context_types: [],
                })
            }
            filterFooter={
                <div className="flex flex-wrap gap-2">
                    {filters.period !== "all" && (
                        <Button
                            size="xs"
                            variant="outline"
                            icon="x"
                            onClick={() =>
                                onFilters({ ...filters, period: "all" })
                            }
                        >
                            {t(`ai_consumption.period_${filters.period}`)}
                        </Button>
                    )}
                    {filters.user_ids.length > 0 && (
                        <Button
                            size="xs"
                            variant="outline"
                            icon="x"
                            onClick={() =>
                                onFilters({ ...filters, user_ids: [] })
                            }
                        >
                            {t("ai_consumption.filter_user")}:{" "}
                            {filters.user_ids.length}
                        </Button>
                    )}
                    {filters.providers.length > 0 && (
                        <Button
                            size="xs"
                            variant="outline"
                            icon="x"
                            onClick={() =>
                                onFilters({ ...filters, providers: [] })
                            }
                        >
                            {filters.providers.join(", ")}
                        </Button>
                    )}
                    {filters.models.length > 0 && (
                        <Button
                            size="xs"
                            variant="outline"
                            icon="x"
                            onClick={() =>
                                onFilters({ ...filters, models: [] })
                            }
                        >
                            {filters.models.join(", ")}
                        </Button>
                    )}
                    {filters.context_types.length > 0 && (
                        <Button
                            size="xs"
                            variant="outline"
                            icon="x"
                            onClick={() =>
                                onFilters({ ...filters, context_types: [] })
                            }
                        >
                            {filters.context_types
                                .map(getContextLabel)
                                .join(", ")}
                        </Button>
                    )}
                </div>
            }
            viewFilters={{
                period: filters.period,
                users: JSON.stringify(filters.user_ids.map(String)),
                providers: JSON.stringify(filters.providers),
                models: JSON.stringify(filters.models),
                contexts: JSON.stringify(filters.context_types),
            }}
            onRestoreView={(saved, search) =>
                onFilters({
                    ...filters,
                    search,
                    period: ["today", "week", "month"].includes(saved.period)
                        ? (saved.period as AiUsageFilters["period"])
                        : "all",
                    user_ids: parseListSelection(saved.users),
                    providers: parseListSelection(saved.providers),
                    models: parseListSelection(saved.models),
                    context_types: parseListSelection(saved.contexts),
                })
            }
            appearance="workspace"
            columns={columns}
            data={itemsWithSearch}
            loading={loading}
            defaultSortKey="created_at"
            defaultSortOrder="desc"
            searchKeys={["_search_text"]}
        />
    );
};
